import { useEffect, useState, type ReactElement } from 'react';
import { Box, Group, Loader, Tabs, Text } from '@mantine/core';
import { Activity, BarChart3 } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import PinnedTilesSection from '@/shared/surfaces/PinnedTilesSection';
import SurfaceSignalGrid from '@/shared/signals/SurfaceSignalGrid';
import EvidenceTable from '@/shared/surfaces/EvidenceTable';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import type { VizAccent } from '@/design/VizAccents';
import type { MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { formatCount } from '@/shared/analytics/MetricFormatting';
import { SurfaceActivityViewModel, type ActivityOrder } from './SurfaceActivityViewModel';

const ACTIVITY_PAGE_SIZE = 25;

interface SurfaceActivityTabsBodyProps {
    surface: AnalyticsSurface;
    /** Accent for the activity grid's saved-view chrome. */
    accent: VizAccent;
    /** One dimmed sentence above the activity grid framing the feed for this surface. */
    activityIntro: string;
    /** localStorage scope for the activity grid's view state, e.g. "usage-activity". */
    persistenceKey: string;
    /** Surface-specific grid columns; defaults to the standard evidence columns. Pass a module-level constant. */
    columns?: MegaGridColumn<InteractionRecord>[];
    /** Which turns survive the record limit (default newest-first). Pass a module-level constant. */
    orderBy?: ActivityOrder;
    /** Describes the kept slice in the truncation caption, e.g. "most recent" (default) or "heaviest". */
    truncationDescriptor?: string;
}

/**
 * The tabbed landing shared by signal surfaces: an Activity grid of every
 * interaction turn (the default view) beside the Analytics signal dashboard.
 * The ambient filter row sits above the tabs so a filter shapes both the feed
 * and the signals. Surfaces adopt it via a thin `registerBody` wrapper.
 */
export default function SurfaceActivityTabsBody({
    surface,
    accent,
    activityIntro,
    persistenceKey,
    columns,
    orderBy,
    truncationDescriptor = 'most recent',
}: SurfaceActivityTabsBodyProps): ReactElement {
    const [tab, setTab] = useState<string | null>('activity');

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <PageHeaderComponent title={surface.title} subtitle={surface.subtitle} />

            <Tabs value={tab} onChange={setTab} keepMounted={false}>
                <Tabs.List mb="lg">
                    <Tabs.Tab value="activity" leftSection={<Activity size={15} />}>
                        Activity
                    </Tabs.Tab>
                    <Tabs.Tab value="analytics" leftSection={<BarChart3 size={15} />}>
                        Analytics
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="activity">
                    {tab === 'activity' && (
                        <SurfaceActivityTab
                            accent={accent}
                            intro={activityIntro}
                            persistenceKey={persistenceKey}
                            columns={columns}
                            orderBy={orderBy}
                            truncationDescriptor={truncationDescriptor}
                        />
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="analytics">
                    {tab === 'analytics' && (
                        <Box>
                            <SurfaceSignalGrid surfaceId={surface.id} />
                            <PinnedTilesSection surface={surface} />
                        </Box>
                    )}
                </Tabs.Panel>
            </Tabs>
        </Box>
    );
}

interface SurfaceActivityTabProps {
    accent: VizAccent;
    intro: string;
    persistenceKey: string;
    columns?: MegaGridColumn<InteractionRecord>[];
    orderBy?: ActivityOrder;
    truncationDescriptor?: string;
}

/** The activity turn feed on its own — reused by surfaces that mount it outside the tab pair (e.g. Behavior's interior nav). */
export function SurfaceActivityTab({ accent, intro, persistenceKey, columns, orderBy, truncationDescriptor = 'most recent' }: SurfaceActivityTabProps): ReactElement {
    const vm = useViewModel(SurfaceActivityViewModel);
    const filters = useAssistFilters();

    useEffect(() => {
        void vm.load(filters, orderBy);
    }, [vm, filters, orderBy]);

    if (vm.loading && vm.records.length === 0) {
        return (
            <Group justify="center" gap="sm" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Loading activity…
                </Text>
            </Group>
        );
    }

    return (
        <Box>
            <Text size="sm" c="dimmed" mb="md">
                {intro}
            </Text>

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            <EvidenceTable records={vm.records} accent={accent} persistenceKey={persistenceKey} columns={columns} clientPageSize={ACTIVITY_PAGE_SIZE} />

            {vm.totalMatched > vm.records.length && (
                <Text size="xs" c="dimmed" mt="sm">
                    Showing the {formatCount(vm.records.length)} {truncationDescriptor} of {formatCount(vm.totalMatched)} matching prompts.
                </Text>
            )}
        </Box>
    );
}
